import { Badge } from '@travellegacy/nexops-design-system';
import { Button } from '@travellegacy/nexops-design-system';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@travellegacy/nexops-design-system';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@travellegacy/nexops-design-system';
import {
  RefreshCw,
  Users,
  MessageCircle,
  Loader2,
  AlertCircle,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  FileText,
  Image,
  FileImage,
  Bot,
} from 'lucide-react';
import { useInstanceStatus, useInstanceGroups, useSyncGroups, useDashboardMetrics } from '../hooks/use-promotion-groups';
import { useState, useMemo } from 'react';
import { GroupMessagesModal } from '../components/GroupMessagesModal';
import type { GroupAIProfile } from '../api/promotion-groups.api';

const CONTENT_ICON: Record<GroupAIProfile['content'], typeof FileText> = {
  texto: FileText,
  texto_imagem: FileImage,
  imagem: Image,
};

type SortField = 'name' | 'type';
type SortDirection = 'asc' | 'desc';

export function PromotionGroupsPage() {
  const { data: instanceStatus } = useInstanceStatus();
  const { data: groupsData, isLoading: isLoadingGroups, error: groupsError } = useInstanceGroups();
  const { data: metricsData, isLoading: isLoadingMetrics } = useDashboardMetrics();
  const syncMutation = useSyncGroups();

  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [selectedGroup, setSelectedGroup] = useState<{ jid: string; name: string } | null>(null);
  const [metricsTab, setMetricsTab] = useState<'today' | 'total'>('today');

  const instance = instanceStatus?.instance;
  const metrics = metricsData?.data;
  const rawGroups = groupsData?.groups || [];

  // Ordenar grupos
  const groups = useMemo(() => {
    const sorted = [...rawGroups].sort((a, b) => {
      if (sortField === 'name') {
        return sortDirection === 'asc'
          ? a.groupName.localeCompare(b.groupName)
          : b.groupName.localeCompare(a.groupName);
      } else if (sortField === 'type') {
        // Ordenar por tipo: Destino > Origem
        const getTypeValue = (group: typeof a) => {
          if (group.isPromotionDestination) return 0;
          if (group.isPromotionSource) return 1;
          return 2;
        };
        const aValue = getTypeValue(a);
        const bValue = getTypeValue(b);
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }
      return 0;
    });
    return sorted;
  }, [rawGroups, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Alternar direção se já estiver ordenando por este campo
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Novo campo, começar com ascendente
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-4 w-4" />;
    }
    return sortDirection === 'asc' ? (
      <ArrowUp className="h-4 w-4" />
    ) : (
      <ArrowDown className="h-4 w-4" />
    );
  };

  const handleSync = () => {
    syncMutation.mutate();
  };

  return (
    <div className="space-y-6">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Grupos de Promoções</h1>
          <p className="text-muted-foreground mt-1">
            Gerencie grupos de origem e destino para promoções automáticas
          </p>
        </div>
        <Button
          onClick={handleSync}
          disabled={syncMutation.isPending || instance?.status !== 'connected'}
          variant="outline"
        >
          {syncMutation.isPending ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4 mr-2" />
          )}
          Sincronizar Grupos
        </Button>
      </div>

      {/* Card de Métricas */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5" />
            Métricas de Promoções
          </CardTitle>
          <CardDescription>
            Estatísticas de mensagens analisadas pela IA
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoadingMetrics ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Carregando métricas...
            </div>
          ) : metrics ? (
            <div className="space-y-4">
              {/* Tabs */}
              <div className="flex gap-2 border-b">
                <button
                  onClick={() => setMetricsTab('today')}
                  className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
                    metricsTab === 'today'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Hoje
                </button>
                <button
                  onClick={() => setMetricsTab('total')}
                  className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
                    metricsTab === 'total'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Total
                </button>
              </div>

              {/* Conteúdo das Tabs */}
              {metricsTab === 'today' ? (
                <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-lg">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm font-medium text-blue-800 dark:text-blue-200">Recebidas</p>
                      <span className="text-2xl font-bold text-blue-600 dark:text-blue-400">{metrics.today.received}</span>
                    </div>
                    <p className="text-xs text-blue-600 dark:text-blue-400">Total capturadas</p>
                  </div>

                  <div className="p-4 bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800 rounded-lg">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm font-medium text-green-800 dark:text-green-200">Aprovadas</p>
                      <span className="text-2xl font-bold text-green-600 dark:text-green-400">{metrics.today.approved}</span>
                    </div>
                    <p className="text-xs text-green-600 dark:text-green-400">Enviadas aos grupos</p>
                  </div>

                  <div className="p-4 bg-gray-50 dark:bg-gray-900/10 border border-gray-200 dark:border-gray-800 rounded-lg">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm font-medium text-gray-800 dark:text-gray-200">Descartadas</p>
                      <span className="text-2xl font-bold text-gray-600 dark:text-gray-400">{metrics.today.discarded}</span>
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Filtradas pela IA</p>
                  </div>

                  <div className="p-4 bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">Para Revisar</p>
                      <span className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{metrics.today.needsReview}</span>
                    </div>
                    <p className="text-xs text-yellow-600 dark:text-yellow-400">Aguardando revisão</p>
                  </div>

                  <div className="p-4 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-lg">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm font-medium text-red-800 dark:text-red-200">Com Erro</p>
                      <span className="text-2xl font-bold text-red-600 dark:text-red-400">{metrics.today.errors}</span>
                    </div>
                    <p className="text-xs text-red-600 dark:text-red-400">Falhas técnicas</p>
                  </div>

                  <div className="p-4 bg-violet-50 dark:bg-violet-900/10 border border-violet-200 dark:border-violet-800 rounded-lg">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm font-medium text-violet-800 dark:text-violet-200">Taxa Aprovação</p>
                      <span className="text-2xl font-bold text-violet-600 dark:text-violet-400">{metrics.today.approvalRate.toFixed(1)}%</span>
                    </div>
                    <p className="text-xs text-violet-600 dark:text-violet-400">Decisões definitivas</p>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-lg">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm font-medium text-blue-800 dark:text-blue-200">Recebidas</p>
                      <span className="text-2xl font-bold text-blue-600 dark:text-blue-400">{metrics.total.received}</span>
                    </div>
                    <p className="text-xs text-blue-600 dark:text-blue-400">Total capturadas</p>
                  </div>

                  <div className="p-4 bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800 rounded-lg">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm font-medium text-green-800 dark:text-green-200">Aprovadas</p>
                      <span className="text-2xl font-bold text-green-600 dark:text-green-400">{metrics.total.approved}</span>
                    </div>
                    <p className="text-xs text-green-600 dark:text-green-400">Enviadas aos grupos</p>
                  </div>

                  <div className="p-4 bg-gray-50 dark:bg-gray-900/10 border border-gray-200 dark:border-gray-800 rounded-lg">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm font-medium text-gray-800 dark:text-gray-200">Descartadas</p>
                      <span className="text-2xl font-bold text-gray-600 dark:text-gray-400">{metrics.total.discarded}</span>
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Filtradas pela IA</p>
                  </div>

                  <div className="p-4 bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">Para Revisar</p>
                      <span className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{metrics.total.needsReview}</span>
                    </div>
                    <p className="text-xs text-yellow-600 dark:text-yellow-400">Aguardando revisão</p>
                  </div>

                  <div className="p-4 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-lg">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm font-medium text-red-800 dark:text-red-200">Com Erro</p>
                      <span className="text-2xl font-bold text-red-600 dark:text-red-400">{metrics.total.errors}</span>
                    </div>
                    <p className="text-xs text-red-600 dark:text-red-400">Falhas técnicas</p>
                  </div>

                  <div className="p-4 bg-violet-50 dark:bg-violet-900/10 border border-violet-200 dark:border-violet-800 rounded-lg">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm font-medium text-violet-800 dark:text-violet-200">Taxa Aprovação</p>
                      <span className="text-2xl font-bold text-violet-600 dark:text-violet-400">{metrics.total.approvalRate.toFixed(1)}%</span>
                    </div>
                    <p className="text-xs text-violet-600 dark:text-violet-400">Decisões definitivas</p>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2 p-4 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-lg text-blue-800 dark:text-blue-200">
              <AlertCircle className="h-4 w-4" />
              <p className="text-sm">Nenhuma métrica disponível no momento.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Card de Lista de Grupos */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Grupos WhatsApp
          </CardTitle>
          <CardDescription>
            {groups.length > 0
              ? `${groups.length} grupo(s) encontrado(s)`
              : 'Nenhum grupo encontrado'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoadingGroups ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin mr-2" />
              Carregando grupos...
            </div>
          ) : groupsError ? (
            <div className="flex items-center gap-2 p-4 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-lg text-red-800 dark:text-red-200">
              <AlertCircle className="h-4 w-4" />
              <p className="text-sm">Erro ao carregar grupos. Tente novamente mais tarde.</p>
            </div>
          ) : groups.length === 0 ? (
            <div className="flex items-center gap-2 p-4 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-lg text-blue-800 dark:text-blue-200">
              <AlertCircle className="h-4 w-4" />
              <p className="text-sm">Nenhum grupo encontrado. Clique em "Sincronizar Grupos" para buscar os grupos do WhatsApp.</p>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[50px]">{' '}</TableHead>
                    <TableHead>
                      <button
                        onClick={() => handleSort('name')}
                        className="flex items-center gap-2 hover:text-foreground transition-colors"
                      >
                        Nome do Grupo
                        {getSortIcon('name')}
                      </button>
                    </TableHead>
                    <TableHead>ID</TableHead>
                    <TableHead>Modelo de IA</TableHead>
                    <TableHead>Conteúdo</TableHead>
                    <TableHead className="text-right">
                      <button
                        onClick={() => handleSort('type')}
                        className="flex items-center gap-2 ml-auto hover:text-foreground transition-colors"
                      >
                        Tipo
                        {getSortIcon('type')}
                      </button>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {groups.map((group) => (
                    <TableRow
                      key={group.groupJid}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => setSelectedGroup({ jid: group.groupJid, name: group.groupName })}
                    >
                      <TableCell>
                        {group.groupPictureUrl ? (
                          <img
                            src={group.groupPictureUrl}
                            alt={group.groupName}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                            <Users className="h-5 w-5 text-muted-foreground" />
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="font-medium">{group.groupName}</TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {group.groupJid}
                      </TableCell>
                      <TableCell>
                        {group.aiProfile ? (
                          <div
                            className="flex items-center gap-1.5 text-sm whitespace-nowrap"
                            title={`${group.aiProfile.model} · prompt: ${group.aiProfile.promptFile}`}
                          >
                            <Bot className="h-4 w-4 text-muted-foreground" />
                            {group.aiProfile.modelLabel}
                            {group.aiProfile.customPrompt && (
                              <Badge variant="outline" className="text-xs">
                                Prompt próprio
                              </Badge>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {group.aiProfile ? (
                          (() => {
                            const ContentIcon = CONTENT_ICON[group.aiProfile.content];
                            return (
                              <div
                                className="flex items-center gap-1.5 text-sm whitespace-nowrap"
                                title={group.aiProfile.contentDetail}
                              >
                                <ContentIcon className="h-4 w-4 text-muted-foreground" />
                                {group.aiProfile.contentLabel}
                              </div>
                            );
                          })()
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end flex-wrap">
                          {group.isPromotionSource && (
                            <Badge variant="info" className="text-xs">
                              Origem
                            </Badge>
                          )}
                          {group.isPromotionDestination && (
                            <Badge variant="success" className="text-xs">
                              Destino
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de Histórico de Mensagens */}
      <GroupMessagesModal
        isOpen={!!selectedGroup}
        onClose={() => setSelectedGroup(null)}
        groupJid={selectedGroup?.jid || null}
        groupName={selectedGroup?.name || ''}
      />
    </div>
  );
}
